import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Eye, Heart, Instagram, Sparkles, Target } from "lucide-react";
import { Logo, MaskLines, Overline, Reveal } from "@/components/site/Primitives";
import { Marquee } from "@/components/site/Marquee";
import { BookingCalendar } from "@/components/site/BookingCalendar";
import { openCookiePrefs } from "@/components/site/CookieBanner";
import { useLang, useT } from "@/lib/i18n";

const ICONS = [Eye, Heart, Target, Sparkles];

const Header = () => {
  const { lang, setLang } = useLang();
  return (
    <header className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="thanks-logo-link">
        <Logo /><span className="font-display text-xl whitespace-nowrap">Lene Mauro</span>
      </Link>
      <div className="flex items-center rounded-full border border-[var(--line)] p-1 text-xs font-mono">
        {["it", "en"].map((l) => (
          <button key={l} data-testid={`thanks-lang-${l}-btn`} onClick={() => setLang(l)}
            className={`px-3 py-1 rounded-full uppercase transition-colors ${lang === l ? "bg-[var(--gold)] text-[#0c0a09]" : "text-[var(--muted)] hover:text-[var(--fg)]"}`}>{l}</button>
        ))}
      </div>
    </header>
  );
};

const Pillars = ({ t }) => (
  <section className="py-24 md:py-32">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <Reveal>
        <Overline>{t.brandOver}</Overline>
        <h2 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">{t.brandTitle}</h2>
        <p className="mt-8 max-w-2xl text-base md:text-lg text-stone-300 leading-relaxed">{t.brandIntro}</p>
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {t.pillars.map((p, i) => {
          const Icon = ICONS[i];
          return (
            <Reveal key={p.t} delay={i * 0.08} className="h-full">
              <div className="bento-cell h-full rounded-[24px] border border-[var(--line)] bg-[var(--card)] p-8" data-testid={`thanks-pillar-${i}`}>
                <Icon className="text-[var(--gold)]" size={26} />
                <h3 className="mt-6 font-display text-3xl">{p.t}</h3>
                <p className="mt-3 text-sm text-stone-300 leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Week = ({ t }) => (
  <section className="py-24 md:py-32 bg-[var(--bg2)]">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <Reveal>
        <Overline>{t.weekOver}</Overline>
        <h2 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">{t.weekTitle}</h2>
        <p className="mt-8 max-w-2xl text-base md:text-lg text-stone-300 leading-relaxed">{t.weekIntro}</p>
      </Reveal>
      <div className="mt-16 border-t border-[var(--line)]">
        {t.days.map((d, i) => (
          <Reveal key={d.day} delay={0.04 * i}>
            <div className="group grid md:grid-cols-12 gap-3 md:gap-6 py-7 md:py-9 border-b border-[var(--line)]" data-testid={`thanks-day-${i}`}>
              <span className="md:col-span-1 font-mono text-sm text-[var(--gold)]">0{i + 1}</span>
              <span className="md:col-span-3 font-display text-3xl md:text-4xl transition-transform duration-500 group-hover:translate-x-2">{d.day}</span>
              <span className="md:col-span-3 font-mono text-xs uppercase tracking-[0.2em] text-stone-300 md:pt-3">{d.focus}</span>
              <p className="md:col-span-5 text-stone-300 leading-relaxed md:pt-2">{d.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div className="mt-14 rounded-[24px] border border-[var(--gold)]/40 bg-gradient-to-b from-[#2a2219] to-[#15120f] p-8 md:p-10">
          <p className="font-display text-3xl">{t.resultTitle}</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {t.results.map((r) => (
              <li key={r} className="flex gap-3 text-stone-200"><Check size={18} className="text-[var(--gold)] mt-0.5 shrink-0" />{r}</li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function ThankYouPage() {
  const t = useT().thanks;
  const cookiePrefs = useT().cookies.prefs;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="site">
      <div className="grain" aria-hidden="true" />
      <Header />
      <section className="relative pt-16 md:pt-24 pb-20 overflow-hidden">
        <div className="hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm text-emerald-300" data-testid="thanks-confirmation">
            <Check size={16} /> {t.badge}
          </div>
          <h1 className="mt-10 font-display font-light leading-[0.98] tracking-[-0.02em] text-[12vw] sm:text-7xl lg:text-[6.2rem]" data-testid="thanks-title">
            <MaskLines lines={t.title} italicIndex={1} delay={0.1} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-stone-300 leading-relaxed">{t.sub}</p>
          </Reveal>
        </div>
      </section>
      <Marquee />
      <BookingCalendar />
      <Pillars t={t} />
      <Week t={t} />
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">{t.ctaTitle}</h2>
            <p className="mt-6 max-w-xl text-stone-300">{t.ctaSub}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="https://www.instagram.com/lenemauro/" target="_blank" rel="noopener noreferrer" data-testid="thanks-instagram-btn" className="btn-gold">
                <Instagram size={18} /> {t.ig} <ArrowUpRight size={18} />
              </a>
              <Link to="/" data-testid="thanks-home-btn" className="btn-ghost"><ArrowLeft size={18} /> {t.home}</Link>
            </div>
          </Reveal>
        </div>
      </section>
      <footer className="py-8 border-t border-[var(--line)] flex justify-center gap-6 font-mono text-xs text-stone-400">
        <span>© {new Date().getFullYear()} Lene Mauro</span>
        <button onClick={openCookiePrefs} data-testid="thanks-cookie-prefs-btn" className="hover:text-[var(--gold)]">{cookiePrefs}</button>
        <Link to="/privacy" data-testid="thanks-privacy-link" className="hover:text-[var(--gold)]">Privacy</Link>
      </footer>
    </div>
  );
}
